import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import {
   BaseStationHandsetInput,
   DepartmentExtensionInput,
   ExtensionAutoAttendantInput,
   ExtensionExternalForwardInput,
   ExtensionFaxInput,
   ExtensionFmFmInput,
   ExtensionHuntGroupInput,
   ExtensionInput,
   ExtensionMailboxInput,
   ExtensionPagingGroupInput,
   ExtensionPhoneInput,
   ExtensionQueueInput,
   ExtensionTypes,
   MailboxInput,
   QueueMemberInput,
} from 'generated/graphql-types';
import { Observable } from 'rxjs';
import {
   createExtensionInput,
   createExtensionUserInput,
   getDeletedAutoAttendantKeyIds,
   getDeletedAutoAttendantPromptIds,
   getDeletedBaseStationHandsetIds,
   getDeletedDepartmentExtensionIds,
   getDeletedExtensionGroupSuperviseeIds,
   getDeletedExtensionSuperviseeIds,
   getDeletedFaxEmailAddressIds,
   getDeletedFmFmStepExtensions,
   getDeletedFmFmStepPhoneNumbers,
   getDeletedFmFmSteps,
   getDeletedHuntGroupExtensionGroupMemberIds,
   getDeletedHuntGroupMemberIds,
   getDeletedNoteIds,
   getDeletedPagingGroupExtensionGroupMemberIds,
   getDeletedPagingGroupMemberIds,
   getDeletedPhoneIds,
   getDeletedQueueGroupExtensionMemberIds,
   getDeletedQueueMemberIds,
   getDeletedQueuePeriodicAnnouncementIds,
   getDeletedTimeBasedRouteIds,
} from 'src/app/common-components/helpers/extension-helper';
import { SaveSipUsers, SaveSipUsersVariables } from '../../device-register/queries/generated/SaveSipUsers';
import { SaveSipUserMutation } from '../../device-register/queries/save-sipuser.graphql';
import { LogOutAgent, LogOutAgentVariables } from '../../queue-history-register/queries/generated/LogOutAgent';
import { logOutAgent } from '../../queue-history-register/queries/log-out-agent.graphql';
import { createExtensionsInput } from '../helpers/presence-contact-helper';
import { DepartmentExtensionItemView } from '../models/department-extension.model';
import { ExtensionDetailSaveModel } from '../models/extension-detail-save.model';
import { ExtensionDetailView } from '../models/extension-detail-view.model';
import { SaveExtension, SaveExtensionVariables } from '../queries/generated/SaveExtension';
import { SaveExtensionPhones, SaveExtensionPhonesVariables } from '../queries/generated/SaveExtensionPhones';
import {
   SavePresenceContactExtension,
   SavePresenceContactExtensionVariables,
} from '../queries/generated/SavePresenceContactExtension';
import { SaveExtensionPhoneMutation } from '../queries/save-extension-phone.graphql';
import { SaveExtensionMutation } from '../queries/save-extension.graphql';
import { SavePresenceContactExtensionsMutation } from '../queries/save-presence-contact-extensions.graphql';

@Injectable({ providedIn: 'root' })
export class ExtensionDetailApiService {
   constructor(private httpClient: HttpClient, private apiService: LcsApiService) {}

   save(
      extensionDetail: ExtensionDetailSaveModel,
      fields: (keyof ExtensionInput)[],
      extensionPhoneFields: (keyof ExtensionPhoneInput)[] | null,
      extensionAutoAttendantFields: (keyof ExtensionAutoAttendantInput)[] | null,
      extensionExternalForwardFields: (keyof ExtensionExternalForwardInput)[] | null,
      extensionHuntGroupFields: (keyof ExtensionHuntGroupInput)[] | null,
      extensionMailboxFields: (keyof ExtensionMailboxInput)[] | null,
      mailboxFields: (keyof MailboxInput)[] | null,
      extensionQueueFields: (keyof ExtensionQueueInput)[] | null,
      extensionFaxFields: (keyof ExtensionFaxInput)[] | null,
      extensionFindMeFollowMeFields: (keyof ExtensionFmFmInput)[] | null,
      extensionPagingGroupFields: (keyof ExtensionPagingGroupInput)[] | null,
      existingExtension?: ExtensionDetailView | null
   ): Observable<ExecutionResult<SaveExtension>> {
      const isSavingNewDepartments =
         extensionDetail.extension.departmentExtensions?.some((dep) => dep.departmentId === 0) ?? false;
      const extension = createExtensionInput(
         extensionDetail,
         fields,
         extensionPhoneFields,
         extensionPagingGroupFields,
         extensionExternalForwardFields,
         extensionAutoAttendantFields,
         extensionHuntGroupFields,
         extensionMailboxFields,
         mailboxFields,
         extensionQueueFields,
         extensionFaxFields,
         extensionFindMeFollowMeFields,
         isSavingNewDepartments
      );
      let deletedPhoneIds: number[] = [];
      let deletedAutoAttendantKeyIds: number[] = [];
      let deletedAutoAttendantPromptIds: number[] = [];
      let deletedHuntGroupMemberIds: number[] = [];
      let deletedTimeBasedRouteIds: number[] = [];
      let deletedExtensionSuperviseeIds: number[] = [];
      let deletedExtensionGroupSuperviseeIds: number[] = [];
      let deletedQueueMemberIds: number[] = [];
      let deletedQueuePeriodicAnnouncementIds: number[] = [];
      let deletedExtensionHuntGroupIds: number[] = [];
      let deletedExtensionPagingGroupIds: number[] = [];
      let deletedFaxEmailAddressIds: number[] = [];
      let deletedDepartmentExtensionIds: number[] = [];
      let deletedBaseStationHandsetIds: number[] = [];
      let deletedFmFmSteps: number[] = [];
      let deletedFmFmStepExtensions: number[] = [];
      let deletedFmFmStepPhoneNumbers: number[] = [];
      let deletedExtensionQueueGroupIds: number[] = [];
      let deletedPagingGroupExtensionGroupIds: number[] = [];
      let deletedPagingGroupMemberIds: number[] = [];
      let deletedNoteIds: number[] = [];
      let deletedAdminNoteId: number | undefined;
      let deletedExtensionNoteId: number | undefined;
      deletedFaxEmailAddressIds = getDeletedFaxEmailAddressIds(extensionDetail.extension.faxEmailAddresses ?? []);

      deletedTimeBasedRouteIds = getDeletedTimeBasedRouteIds(extensionDetail.extension.timeBasedRoutes ?? []);

      deletedDepartmentExtensionIds = getDeletedDepartmentExtensionIds(
         extensionDetail.extension.departmentExtensions ?? []
      );

      extension.users = createExtensionUserInput(
         extensionDetail.extension.users ?? null,
         existingExtension?.usersConnection?.items ?? null,
         extension.users
      );

      if (extensionDetail.extension.adminNote)
         deletedAdminNoteId = getDeletedNoteIds(extensionDetail.extension.adminNote);
      if (extensionDetail.extension.extensionNote) {
         deletedExtensionNoteId = getDeletedNoteIds(extensionDetail.extension.extensionNote);
      }
      deletedNoteIds = [
         ...(deletedAdminNoteId !== undefined ? [deletedAdminNoteId] : []),
         ...(deletedExtensionNoteId !== undefined ? [deletedExtensionNoteId] : []),
      ];

      if (extension.type === ExtensionTypes.Phone) {
         deletedPhoneIds = getDeletedPhoneIds(
            extensionDetail.extension.extensionPhone?.phones ?? [],
            existingExtension?.extensionPhone?.phonesConnection.items ?? []
         );
         deletedExtensionSuperviseeIds = getDeletedExtensionSuperviseeIds(
            extensionDetail.extension.extensionPhone?.extensionSupervisees ?? null,
            existingExtension?.extensionPhone?.extensionSuperviseesConnection.items ?? null
         );
         deletedExtensionGroupSuperviseeIds = getDeletedExtensionGroupSuperviseeIds(
            extensionDetail.extension.extensionPhone?.extensionGroupSupervisees ?? null,
            existingExtension?.extensionPhone?.extensionGroupSuperviseesConnection.items ?? null
         );
         const currentBaseStationHandsets = extensionDetail.extension.extensionPhone?.phones?.reduce((acc, phone) => {
            const handsets = phone.baseStationHandsets ?? [];
            return acc.concat(handsets);
         }, [] as BaseStationHandsetInput[]);
         deletedBaseStationHandsetIds = getDeletedBaseStationHandsetIds(
            currentBaseStationHandsets ?? null,
            existingExtension?.extensionPhone?.phonesConnection.items ?? null,
            deletedPhoneIds
         );
      } else if (extension.type === ExtensionTypes.AutoAttendant) {
         deletedAutoAttendantKeyIds = getDeletedAutoAttendantKeyIds(
            extensionDetail.extension.extensionAutoAttendant?.autoAttendantKeys ?? []
         );

         deletedAutoAttendantPromptIds = getDeletedAutoAttendantPromptIds(
            extensionDetail.extension.extensionAutoAttendant?.autoAttendantPrompts ?? []
         );
      } else if (extension.type === ExtensionTypes.HuntGroup) {
         deletedHuntGroupMemberIds = getDeletedHuntGroupMemberIds(
            extensionDetail.extension.extensionHuntGroup?.huntGroupMembers ?? null,
            existingExtension?.extensionHuntGroup?.huntGroupMembersConnection.items ?? null
         );
         deletedExtensionHuntGroupIds = getDeletedHuntGroupExtensionGroupMemberIds(
            extensionDetail.extension.extensionHuntGroup?.extensionHuntGroups ?? null,
            existingExtension?.extensionHuntGroup?.extensionGroupHuntGroupMembersConnection.items ?? null
         );
      } else if (extension.type === ExtensionTypes.CallQueue) {
         deletedQueueMemberIds = getDeletedQueueMemberIds(
            extensionDetail.extension.extensionQueue?.queueMembers ?? null,
            existingExtension?.extensionQueue?.queueMembersConnection.items ?? null
         );
         deletedQueuePeriodicAnnouncementIds = getDeletedQueuePeriodicAnnouncementIds(
            extensionDetail.extension.extensionQueue?.queuePeriodicAnnouncements ?? []
         );
         deletedExtensionQueueGroupIds = getDeletedQueueGroupExtensionMemberIds(
            extensionDetail.extension.extensionQueue?.queueGroupMembers ?? null,
            existingExtension?.extensionQueue?.queueExtensionGroupMembersConnection.items ?? null
         );
      } else if (extension.type === ExtensionTypes.FindMeFollowMe) {
         deletedFmFmSteps = getDeletedFmFmSteps(
            extensionDetail.extension.extensionFindMeFollowMe?.extensionFmFmSteps ?? []
         );
         deletedFmFmStepExtensions = getDeletedFmFmStepExtensions(
            extensionDetail.extension.extensionFindMeFollowMe?.extensionFmFmSteps ?? []
         );
         deletedFmFmStepPhoneNumbers = getDeletedFmFmStepPhoneNumbers(
            extensionDetail.extension.extensionFindMeFollowMe?.extensionFmFmSteps ?? []
         );
      } else if (extension.type === ExtensionTypes.PagingGroup) {
         deletedPagingGroupMemberIds = getDeletedPagingGroupMemberIds(
            extensionDetail.extension.extensionPagingGroup?.pagingGroupMembers ?? null,
            existingExtension?.extensionPagingGroup?.pagingGroupMembersConnection.items ?? null
         );
         deletedPagingGroupExtensionGroupIds = getDeletedPagingGroupExtensionGroupMemberIds(
            extensionDetail.extension.extensionPagingGroup?.pagingGroupExtensionGroups ?? null,
            existingExtension?.extensionPagingGroup?.pagingGroupExtensionGroupsConnection.items ?? null
         );
      }

      const variables: SaveExtensionVariables = {
         deletedAutoAttendantKeyIds,
         deletedAutoAttendantPromptIds,
         deletedHuntGroupMemberIds,
         deletedPhoneIds,
         deletedQueueMemberIds,
         deletedQueuePeriodicAnnouncementIds,
         deletedTimeBasedRouteIds,
         deletedExtensionHuntGroupIds,
         deletedExtensionSuperviseeIds,
         deletedExtensionGroupSuperviseeIds,
         deletedFaxEmailAddressIds,
         deletedDepartmentExtensionIds,
         deletedExtensionQueueGroupIds,
         deletedFmFmSteps,
         deletedFmFmStepExtensions,
         deletedFmFmStepPhoneNumbers,
         deletedBaseStationHandsetIds,
         deletedPagingGroupExtensionGroupIds,
         deletedPagingGroupMemberIds,
         deletedNoteIds,
         extension: extension,
         hasDeletedHuntGroupMembers: deletedHuntGroupMemberIds.length > 0,
         hasDeletedKeys: deletedAutoAttendantKeyIds.length > 0,
         hasDeletedPeriodicAnnouncements: deletedQueuePeriodicAnnouncementIds.length > 0,
         hasDeletedPhones: deletedPhoneIds.length > 0,
         hasDeletedPrompts: deletedAutoAttendantPromptIds.length > 0,
         hasDeletedQueueMembers: deletedQueueMemberIds.length > 0,
         hasDeletedTimeBasedRoutes: deletedTimeBasedRouteIds.length > 0,
         hasDeletedExtensionSupervisees: deletedExtensionSuperviseeIds.length > 0,
         hasDeletedExtensionGroupSupervisees: deletedExtensionGroupSuperviseeIds.length > 0,
         hasDeletedFaxEmailAddresses: deletedFaxEmailAddressIds.length > 0,
         hasDeletedDepartmentExtensions: deletedDepartmentExtensionIds.length > 0,
         hasDeletedFmFmSteps: deletedFmFmSteps.length > 0,
         hasDeletedExtensionQueueGroups: deletedExtensionQueueGroupIds.length > 0,
         hasDeletedFmFmStepExtensions: deletedFmFmStepExtensions.length > 0,
         hasDeletedFmFmStepPhoneNumbers: deletedFmFmStepPhoneNumbers.length > 0,
         hasDeletedBaseStationHandsets: deletedBaseStationHandsetIds.length > 0,
         hasDeletedExtensionHuntGroups: deletedExtensionHuntGroupIds.length > 0,
         hasDeletedPagingGroupMembers: deletedPagingGroupMemberIds.length > 0,
         hasDeletedPagingGroupExtensionGroups: deletedPagingGroupExtensionGroupIds.length > 0,
         hasDeletedNotes: deletedNoteIds.length > 0,
      };

      return this.apiService.query<SaveExtension, SaveExtensionVariables>({
         query: SaveExtensionMutation,
         variables: variables,
      });
   }

   changeSipUserPasswords(sipUserIds: number[]): Observable<ExecutionResult<SaveSipUsers>> {
      const sipUsers = sipUserIds.map((sipUserId) => ({
         sipUserId: sipUserId,
         resetSipUserPassword: true,
         fields: ['resetSipUserPassword'],
      }));

      return this.apiService.query<SaveSipUsers, SaveSipUsersVariables>({
         query: SaveSipUserMutation,
         variables: {
            sipUsers: sipUsers,
         },
      });
   }

   changeExtensionPresenceData(
      dndStatus: string,
      office: string,
      extensionPhoneId: number,
      extensionId: number,
      departmentExtensions: DepartmentExtensionInput[],
      fields: string[]
   ): Observable<ExecutionResult<SavePresenceContactExtension> | ExecutionResult<SaveExtensionPhones>> {
      const extension = createExtensionsInput(dndStatus, office, extensionPhoneId, extensionId, departmentExtensions);

      let deletedDepartmentExtensionIds: number[] = getDeletedDepartmentExtensionIds(
         departmentExtensions as unknown as DepartmentExtensionItemView[]
      );
      const variables: SavePresenceContactExtensionVariables = {
         deletedDepartmentExtensionIds,
         extension,
         hasDeletedDepartmentExtensions: deletedDepartmentExtensionIds.length > 0,
      };
      if (fields.length === 1 && fields[0] === 'dndStatus') {
         return this.apiService.query<SaveExtensionPhones, SaveExtensionPhonesVariables>({
            query: SaveExtensionPhoneMutation,
            variables: {
               extensionPhones: [
                  {
                     dndStatus: dndStatus,
                     extensionPhoneId: extensionPhoneId,
                     fields: fields,
                  },
               ],
            },
         });
      }
      return this.apiService.query<SavePresenceContactExtension, SavePresenceContactExtensionVariables>({
         query: SavePresenceContactExtensionsMutation,
         variables: variables,
      });
   }

   resetMwi(accountId: number | undefined, extensionId: number | undefined): Observable<any> {
      const mailboxPostBody = {
         AccountId: accountId ? accountId : null,
         ExtensionId: extensionId ? extensionId : null,
      };
      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json',
         }),
      };

      const url = this.apiService.buildApiUrl('Mailboxes/ResetMwi');
      return this.httpClient.post(url, mailboxPostBody, httpOptions);
   }

   logAgentOutOfQueue(queueMemberId: number): Observable<any> {
      const queueMember: QueueMemberInput = {
         queueMemberId: queueMemberId,
         isLoggedIn: false,
         fields: ['IsLoggedIn'],
      };
      return this.apiService.query<LogOutAgent, LogOutAgentVariables>({
         query: logOutAgent,
         variables: { queueMember },
      });
   }

   deleteExtension(extensionId: number): Observable<ExecutionResult<void>> {
      const variables: SaveExtensionVariables = {
         deletedAutoAttendantKeyIds: [],
         deletedAutoAttendantPromptIds: [],
         deletedHuntGroupMemberIds: [],
         deletedPhoneIds: [],
         deletedFaxEmailAddressIds: [],
         deletedDepartmentExtensionIds: [],
         deletedQueueMemberIds: [],
         deletedQueuePeriodicAnnouncementIds: [],
         deletedTimeBasedRouteIds: [],
         deletedExtensionSuperviseeIds: [],
         deletedExtensionHuntGroupIds: [],
         deletedExtensionGroupSuperviseeIds: [],
         deletedPagingGroupExtensionGroupIds: [],
         deletedPagingGroupMemberIds: [],
         deletedFmFmSteps: [],
         deletedExtensionQueueGroupIds: [],
         deletedFmFmStepExtensions: [],
         deletedFmFmStepPhoneNumbers: [],
         deletedBaseStationHandsetIds: [],
         deletedNoteIds: [],
         hasDeletedFaxEmailAddresses: false,
         hasDeletedHuntGroupMembers: false,
         hasDeletedKeys: false,
         hasDeletedPeriodicAnnouncements: false,
         hasDeletedPhones: false,
         hasDeletedPrompts: false,
         hasDeletedQueueMembers: false,
         hasDeletedTimeBasedRoutes: false,
         hasDeletedNotes: false,
         extension: {
            extensionId: extensionId,
            isArchived: true,
            fields: ['IsArchived'],
         },
      };
      return this.apiService.query<void, SaveExtensionVariables>({
         query: SaveExtensionMutation,
         variables: variables,
      });
   }
}
